import { useCallback, useEffect, useRef } from 'react'
import { pick, uniq } from 'lodash-es'
import {
  useRealAmap,
  clusterMarkersPlugin,
  polysPlugin,
  boundsPlugin,
  infoWindowPlugin,
  Layer,
  iconMap,
  useInfoWindowPortal,
} from '@ow/maps/lib'
import { useProjectStore, useTbViewStore, useAppStore } from '@vt/hooks'
import { mergeQueries, getDeepPath } from '@ow/utils'
import {
  defaultMapConfig,
  getLegendFilter,
  isSkipLegend,
  usePageDataLoader,
  getLayers,
} from './utils'

const useVtAmap = (
  mapId,
  {
    center,
    showTbDetail,
    showMarker = true,
    showArea = true,
    showLabel = false,
    onPolygonClick,
    areaFields,
  }
) => {
  const getVtProject = useProjectStore((state) => state.getVtProject)
  const { amapKey = '0e4f7b5e424e7db93755cb5d48e71a75' } = getVtProject()

  const getDitto = useAppStore((state) => state.getDitto)

  const areaDitto = getDitto('area')

  const {
    ditto: tbDitto,
    filters,
    mapConfig = defaultMapConfig,
  } = useTbViewStore((state) => pick(state, ['ditto', 'filters', 'mapConfig']))

  const clusterMarkersLayer = useRef(new Layer(null, {}))
  const polygonsLayer = useRef(new Layer(null, {}))
  const infoWindowLayer = useRef(new Layer(null, {}))

  const getTbDataByPages = usePageDataLoader()

  const { getApis, inited, getAmap } = useRealAmap(mapId, {
    amapKey,
    plugins: [
      boundsPlugin(),
      clusterMarkersPlugin(),
      polysPlugin(),
      infoWindowPlugin(),
    ],
    zoom: mapConfig.zoom,
    zooms: mapConfig.zooms,
    center: center || mapConfig.center,
    getLayers: getLayers(mapConfig),
  })

  const { containerRef: infoWindowContainer, InfoWindow } =
    useInfoWindowPortal()

  const getPolygonClick = useCallback(() => {
    if (!onPolygonClick) return undefined
    return ({ originData, lnglat }) => {
      onPolygonClick(originData)
      infoWindowLayer.current.open(lnglat)
    }
  }, [])

  const onMarkClick = (ctx) => {
    if (ctx.count === 1 && showTbDetail) {
      showTbDetail({ currentData: ctx.data[0] })
    }
  }

  // 获取坐标数据
  const loadMarkerData = useCallback(
    async (finalFilters, legend, cacheFlag) => {
      getTbDataByPages(legend.value)(
        tbDitto,
        {
          filters: finalFilters,
          fields: ['tableDataId', 'lnglat'],
          pageSize: 500,
        },
        (data) => {
          clusterMarkersLayer.current.render(
            data,
            {
              icon: iconMap[legend.markerIcon],
              labelRender: showLabel ? (item) => item.tableDataId : null,
              onClick: onMarkClick,
            },
            cacheFlag
          )
        }
      )
    },
    [tbDitto]
  )

  // 获取点位地块数据
  const loadAreaData = useCallback(
    async (finalFilters) => {
      const { levelConfig = {}, pageSize = 500 } = mapConfig
      const getPolygonData = (data) => {
        const polygonData = Array.from({ length: 10 }).map(() => [])

        data.forEach((item) => {
          if (!item.geoInfo) return
          const { level = 0, objectId } = item
          item.geoInfo.forEach((geo, i) => {
            if (!Array.isArray(geo?.coordinates)) return

            if (geo.type === 'Polygon') {
              polygonData[level].push({
                ...item,
                ...levelConfig[level],
                path: getDeepPath(geo.coordinates),
                objectId: `${objectId}-${i}`,
              })
            } else if (geo.type === 'MultiPolygon') {
              geo.coordinates.forEach((path, j) => {
                polygonData[level].push({
                  ...item,
                  ...levelConfig[level],
                  path,
                  objectId: `${objectId}-${i}-${j}`,
                })
              })
            }
          })
        })

        return polygonData
      }

      const fields = areaFields
        ? uniq([...areaFields, 'geoInfo', 'level'])
        : null

      getTbDataByPages('areaDitto')(
        areaDitto,
        {
          filters: finalFilters,
          pageSize,
          fields,
        },
        (data) => {
          const polygonData = getPolygonData(data)

          polygonsLayer.current.render(polygonData.flat(1), {
            onClick: getPolygonClick(),
          })
        }
      )
    },
    [areaDitto]
  )

  useEffect(() => {
    if (inited) {
      const { initClusterMarker, initPolys, initInfoWindow } = getApis()
      initInfoWindow(infoWindowLayer.current, infoWindowContainer.current)
      initClusterMarker(clusterMarkersLayer.current)
      initPolys(polygonsLayer.current)
    }
  }, [inited])

  useEffect(() => {
    if (!inited) return
    if (!showMarker) return

    const legends = mapConfig.mainLayer.legends.filter(
      (legend) => !isSkipLegend(legend.queries, filters?.queries)
    )
    if (!legends.length) {
      clusterMarkersLayer.current.clear()
    }

    legends.forEach((legend) => {
      loadMarkerData(
        mergeQueries(getLegendFilter(legend.queries), filters),
        legend,
        filters
      )
    })
  }, [inited, filters])

  useEffect(() => {
    if (!showArea) return

    loadAreaData({
      logicType: 'and',
      queries: [
        {
          slug: 'geoInfo',
          queryType: ['notNull'],
        },
      ],
    })
  }, [showArea])

  return {
    polygons: polygonsLayer,
    InfoWindow,
    getAmap,
  }
}

export default useVtAmap
